import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Spinner, Center } from "@chakra-ui/react";
import DocumentEditorView from "./DocumentEditorView";
import { useAuth } from "../AuthContext";

/** Detect an "empty" tiptap doc */
const isEmptyDoc = (raw) => {
  if (!raw) return true;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t.length === 0 || t === "{}" || t === "null";
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return Object.keys(raw).length === 0;
  }
  return false;
};

/** Parse content that may already be an object or a JSON string. */
const parseTiptapContent = (content) => {
  if (!content) return null;
  if (typeof content === "object") return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

/**
 * Pick which document to load from the contract.documents array.
 * Priority:
 *  0) if documentId provided (route or querystring), use that
 *  1) status === 'active'
 *  2) newest by createdDate
 *  3) first item
 */
const pickContractDocument = (documents = [], preferredId) => {
  if (!Array.isArray(documents) || documents.length === 0) return null;

  if (preferredId) {
    const byId = documents.find((d) => d?.id === preferredId);
    if (byId) return byId;
  }

  const active =
    documents.find((d) => String(d?.status || "").toLowerCase() === "active") ||
    null;
  if (active) return active;

  const newest =
    [...documents]
      .filter((d) => !!d?.createdDate)
      .sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      )[0] || null;

  return newest || documents[0] || null;
};

export default function DocumentEditorLoader({ userRole }) {
  const { user } = useAuth();
  const { contractId, productCode, documentId: documentIdFromRoute } = useParams();
  const [searchParams] = useSearchParams();
  const documentIdFromQuery = searchParams.get("doc");

  const preferredDocId = documentIdFromRoute || documentIdFromQuery || null;

  // Extract user roles from platformUser
  const userRoles = user?.platformUser?.roles || [];

  const [contract, setContract] = useState(null);
  const [docFromRepo, setDocFromRepo] = useState(null);
  const [repoCodified, setRepoCodified] = useState([]);
  const [docMeta, setDocMeta] = useState(null);
  const [docLoading, setDocLoading] = useState(false);

  // Detect if we're in template mode (contractId === "template")
  const isTemplateMode = contractId === "template";

  // 1) Load contract (skip in template mode)
  useEffect(() => {
    if (isTemplateMode) {
      // For template mode, create a fake contract object
      setContract({
        _id: "template",
        productCode: productCode,
        documents: documentIdFromQuery
          ? [
              {
                id: crypto.randomUUID(),
                documentRepositoryId: documentIdFromQuery,
                status: "template",
              },
            ]
          : [],
      });
      return;
    }

    if (!contractId || !productCode) return;

    const ac = new AbortController();
    (async () => {
      try {
        const url = `https://poc.mrc.ai/${productCode}/api/contracts/${contractId}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch contract");
        const data = await res.json();

        const merged = {
          _id: data?._id ?? contractId,
          productCode: data?.productCode ?? productCode,
          ...data,
        };
        setContract(merged);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading contract:", err);
          setContract({ _id: contractId, productCode, documents: [] });
        }
      }
    })();
    return () => ac.abort();
  }, [contractId, productCode, isTemplateMode, documentIdFromQuery]);

  // 2) Load the chosen document from DocumentsAPI
  useEffect(() => {
    if (!contract) {
      console.log("No contract yet; skipping document load.");
      return;
    }

    const chosen = pickContractDocument(contract.documents, preferredDocId);
    const repoId = chosen?.documentRepositoryId;

    // Reset previous state
    setDocFromRepo(null);
    setDocMeta(chosen || null);

    if (!repoId) {
      console.warn("No documentRepositoryId found; cannot fetch document.");
      return; // no documents to fetch
    }

    const ac = new AbortController();
    setDocLoading(true);
    (async () => {
      try {
        const url = `https://documents.poc.mrc.ai/api/documents/${repoId}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch document from repo");
        const docJson = await res.json();

        // Expect { content: TipTapJson | string, ... }
        const parsed = parseTiptapContent(docJson?.content);
        if (parsed) {
          setDocFromRepo(parsed);
        } else {
          console.warn("Document content was empty or unparsable.");
          setDocFromRepo(null);
        }

        setRepoCodified(Array.isArray(docJson?.codifiedContents) ? docJson.codifiedContents : []);

        if (typeof console !== 'undefined' && console.log) {
          console.log("[LOADER] Fetched document with codifySpecifications:", docJson?.codifySpecifications);
        }

        setDocMeta((prev) => ({
          ...prev,
          _repoFetch: {
            id: docJson?._id,
            status: docJson?.status,
            documentRepositoryId: docJson?.documentRepositoryId,
            definitionId: docJson?.definitionId,
            createdDate: docJson?.createdDate,
            codifyCollection: docJson?.codifyCollection,
            dataStandard: docJson?.dataStandard,
            dataType: docJson?.dataType,
            model: docJson?.model,
            name: docJson?.name,
            productCode: docJson?.productCode,
            isTemplate: docJson?.isTemplate,
            codifySpecifications: docJson?.codifySpecifications,
          },
        }));
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading document from DocumentsAPI:", err);
          setDocFromRepo(null);
        }
      } finally {
        setDocLoading(false);
      }
    })();

    return () => ac.abort();
  }, [contract, preferredDocId]);

  // 3) Decide effective doc
const effectiveDoc = useMemo(() => {
  if (docFromRepo && !isEmptyDoc(docFromRepo)) return docFromRepo;
}, [docFromRepo]);


  // 4) Save hook back to Documents API with coordination
const handleSave = async (payload) => {
  const repoId = docMeta?.documentRepositoryId || docMeta?._repoFetch?.id;
  if (!repoId) {
    console.error("[DocumentEditorLoader] No documentRepositoryId to save against:", { docMeta });
    return { ok: false, message: "No documentRepositoryId to save against." };
  }

  console.log("[DocumentEditorLoader] handleSave called:", {
    repoId,
    payloadKeys: Object.keys(payload || {}),
    payloadName: payload?.name,
    definitionId: payload?.definitionId
  });

  try {
    const res = await fetch(`https://documents.poc.mrc.ai/api/documents/${repoId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload), // ✅ send { definitionId, name, content, codifiedContents, createdDate }
    });

    console.log("[DocumentEditorLoader] Save response:", {
      ok: res.ok,
      status: res.status,
      statusText: res.statusText
    });

    if (!res.ok) {
      const errorText = await res.text().catch(() => "");
      console.error("[DocumentEditorLoader] Save failed:", {
        status: res.status,
        errorText
      });
      throw new Error(errorText || "Failed to save document");
    }

    const savedDoc = await res.json().catch(() => null);
    console.log("[DocumentEditorLoader] Document saved successfully:", {
      repoId,
      savedDocId: savedDoc?._id,
      name: savedDoc?.name
    });

    return { ok: true, data: savedDoc };
  } catch (err) {
    console.error("[DocumentEditorLoader] Save exception:", err);
    return { ok: false, message: err?.message || "Save failed" };
  }
};


  // 5) Loading gate
  const stillBootstrapping = !contract || docLoading || !effectiveDoc;

  if (stillBootstrapping) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // 6) Render editor
  return (
    <DocumentEditorView
      tiptapDocument={effectiveDoc}
      userRole={userRole}
      userRoles={userRoles}
      contract={contract}
      documentMeta={{
        documentId: docMeta?.id || null,
        documentRepositoryId: docMeta?.documentRepositoryId || null,
        definitionId: docMeta?.definitionId || docMeta?._repoFetch?.definitionId || null,
        status: docMeta?.status || null,
        createdDate: docMeta?.createdDate || docMeta?._repoFetch?.createdDate || null,
        codifyCollection: docMeta?.codifyCollection || docMeta?._repoFetch?.codifyCollection || null,
        dataStandard: docMeta?.dataStandard || docMeta?._repoFetch?.dataStandard || null,
        dataType: docMeta?.dataType || docMeta?._repoFetch?.dataType || null,
        model: docMeta?.model || docMeta?._repoFetch?.model || null,
        name: docMeta?.name || docMeta?._repoFetch?.name || null,
        productCode: docMeta?.productCode || docMeta?._repoFetch?.productCode || contract?.productCode || null,
        isTemplate: docMeta?.isTemplate ?? docMeta?._repoFetch?.isTemplate ?? null,
        codifySpecifications: docMeta?.codifySpecifications || docMeta?._repoFetch?.codifySpecifications || [],
      }}
      initialCodifiedContents={repoCodified}
      onSave={handleSave}
    />
  );
}
